import {
  SensorModelConfiguration,
  MetricConfiguration,
  SensorModelKey
} from './types';
import {
  DRAGINO_LHT65,
  MILESIGHT_EM320_TH,
  MILESIGHT_TS201,
  MILESIGHT_WS301,
  MILESIGHT_CT101
} from './models';

/**
 * Service de registre des configurations de modèles de capteurs
 * Permet d'accéder facilement aux configurations des différents modèles
 */
export class SensorModelRegistry {
  private static configs: Map<SensorModelKey, SensorModelConfiguration> | null = null;

  /**
   * Initialise le registre avec toutes les configurations disponibles
   */
  private static initialize(): void {
    if (this.configs !== null) {
      return; // Déjà initialisé
    }

    this.configs = new Map();

    // Enregistrer toutes les configurations
    const allConfigs: SensorModelConfiguration[] = [
      DRAGINO_LHT65,
      MILESIGHT_EM320_TH,
      MILESIGHT_TS201,
      MILESIGHT_WS301,
      MILESIGHT_CT101
    ];

    allConfigs.forEach(config => {
      const key: SensorModelKey = `${config.brand}:${config.model}`;
      this.configs!.set(key, config);
    });
  }

  /**
   * Génère une clé unique pour un couple brand/model
   */
  private static generateKey(brand: string, model: string): SensorModelKey {
    return `${brand}:${model}`;
  }

  /**
   * Récupère la configuration complète d'un modèle de capteur
   * @param brand Marque du capteur
   * @param model Modèle du capteur
   * @returns Configuration du modèle ou null si non trouvé
   */
  static getConfiguration(brand: string, model: string): SensorModelConfiguration | null {
    this.initialize();
    const key = this.generateKey(brand, model);
    return this.configs!.get(key) || null;
  }

  /**
   * Récupère la configuration d'une métrique spécifique
   * @param brand Marque du capteur
   * @param model Modèle du capteur
   * @param metricKey Clé de la métrique (ex: "temperature")
   * @returns Configuration de la métrique ou null si non trouvée
   */
  static getMetricConfiguration(
    brand: string,
    model: string,
    metricKey: string
  ): MetricConfiguration | null {
    const config = this.getConfiguration(brand, model);
    if (!config) {
      return null;
    }
    return config.metrics[metricKey] || null;
  }

  /**
   * Récupère toutes les clés de métriques supportées par un modèle
   * @param brand Marque du capteur
   * @param model Modèle du capteur
   * @returns Tableau des clés de métriques ou tableau vide si modèle non trouvé
   */
  static getMetricKeys(brand: string, model: string): string[] {
    const config = this.getConfiguration(brand, model);
    if (!config) {
      return [];
    }
    return Object.keys(config.metrics);
  }

  /**
   * Vérifie si un modèle de capteur est supporté (a une configuration)
   * @param brand Marque du capteur
   * @param model Modèle du capteur
   * @returns true si le modèle est configuré, false sinon
   */
  static isModelSupported(brand: string, model: string): boolean {
    return this.getConfiguration(brand, model) !== null;
  }

  /**
   * Récupère toutes les configurations de modèles disponibles
   * @returns Tableau de toutes les configurations
   */
  static getSupportedModels(): SensorModelConfiguration[] {
    this.initialize();
    return Array.from(this.configs!.values());
  }

  /**
   * Récupère toutes les métriques disponibles avec leurs configurations
   * pour un modèle donné
   * @param brand Marque du capteur
   * @param model Modèle du capteur
   * @returns Dictionnaire des métriques ou objet vide si modèle non trouvé
   */
  static getMetrics(brand: string, model: string): Record<string, MetricConfiguration> {
    const config = this.getConfiguration(brand, model);
    if (!config) {
      return {};
    }
    return config.metrics;
  }
}
